/**
 * @module authenticationManager
 */
import { Validator } from "jsonschema";
import { loginSchema, playbackEntitlementBodySchema, phoneSchema, resetPasswordSchema } from "../../../schema";
import { services } from "../../../constants";
import errors from "../../errors";
import { geolocation, playbackToken, token, phoneVerificationData, localUserId, layoutTokenStore } from "../../store";
import { cache } from "../../persistence/cache";
import { toUserCache, clearUserCache } from "../user/userCacheDecorators";
import { getErrorTrace } from "../../../errors/traces";
import { isTimeoutError } from "../../../errors/util";
import { TimeoutError } from "../../../errors/timeoutError";
import { memoryCache } from "../../persistence/memoryCache";
import { getSignupMethod } from "./signup/signupMethodSelector";
import { getLoginMethod } from "./login/loginMethodSelector";
import { socialProviders } from "./socialProviders";
import { socialAuthenticationErrors } from "./facebookAuthentication/errors";
import verifyLandline from "./landline/landlineVerification";
import userEligibility from "./userEligibility/userEligibility";
import ebillingServices from "./ebillingServices/ebillingServices";
import userExists from "./userExists/userExists";
import { updateUserData } from "./tokenOperations";
import {
  handleAutologinDeprecation,
  normalizeSignupUsernameParam,
  normalizeLoginUsernameParam
} from "./backwardsCompatibility";
import isBrowser from "../../../utils/isBrowser";
import { t } from "web-utils-strings";

const validator = new Validator();

/**
 * Validates the object which will be sent in /login request.
 * @param {Object} loginInfo - Login request body object. Needs to be 'null' by default to
 * avoid 'undefined' parameter passes validation.
 * @private
 */
function loginValidation(loginInfo = null) {
  if (
    !loginInfo ||
    ((loginInfo.username || loginInfo.password) && (!loginInfo.username || !loginInfo.password)) ||
    (!loginInfo.username && !loginInfo.password && !loginInfo.provider)
  ) {
    throw Error(errors.authentication.login.formatError);
  }
}

/**
 * Validates the object which will be sent in /playbackEntitlement request.
 * @param {Object} playbackData - Playback token request body object. Needs to be 'null' by default to
 * avoid 'undefined' parameter passes validation.
 * @private
 */
function playbackRequestDataValidation(playbackData = null) {
  validator.addSchema(playbackEntitlementBodySchema, "playbackEntitlementBody");
  const validation = validator.validate(playbackData, playbackEntitlementBodySchema);

  if (validation.errors.length) {
    throw Error(`${errors.authentication.playbackToken.formatError} - ${validation.errors.join(" ")}`);
  }
}

/**
 * Sends an error report on playback issues.
 *
 * @param {Object} errorTrace - An object on error trace, including its code and other data.
 * @param {String} additionalErrorMessage - Any other error message to be sent in the report.
 * @private
 */
function sendPlaybackErrorReport(errorTrace, additionalErrorMessage) {
  this.reportsManager.sendReport.call(
    this,
    { env: this.environment, level: "error", event: "Playback" },
    {
      report: errorTrace.code,
      status: "error",
      parameter: {
        errorCode: errorTrace.code,
        dump: {
          ...errorTrace.dump,
          trace: additionalErrorMessage || errorTrace.dump.trace,
          placeInCode: errorTrace.dump.placeInCode
        }
      }
    }
  );
}

/**
 * Gets playback token after validating request body object.
 * @param {Object} requestBody - Playback token request body object.
 */
async function getPlaybackToken(requestBody) {
  playbackRequestDataValidation(requestBody);
  try {
    const response = await this.http.post(
      `${this.backendHosts.auth}${services.playbackToken}`,
      requestBody,
      await this.getHeaders.call(this)
    );
    playbackToken.update(response.data.playbackToken);
    return response.data.playbackToken;
  } catch (error) {
    let requestError;
    if (isTimeoutError(error)) {
      const errorTrace = getErrorTrace("playbackRequestTimeout");
      errorTrace.dump.placeInCode = "Authentication SDK Module - getPlaybackToken";
      errorTrace.dump.timedOutCall = "getPlaybackToken";
      requestError = new TimeoutError(`${errors.player.PLAYBACK_TOKEN_REQUEST_TIMEOUT} - ${error.message}`);
      sendPlaybackErrorReport.call(this, errorTrace);
    } else {
      requestError = new Error();
      const errorTrace = getErrorTrace("playbackTokenRequestError");
      requestError.message = `${errors.authentication.mediaAccessToken.GENERIC_ERROR} - ${error.message}`;
      requestError.code = errorTrace.code;
      this.handleErrorResponse(error);
      sendPlaybackErrorReport.call(this, errorTrace, requestError.message);
    }
    throw requestError;
  }
}

/**
 * Starts a user session by performing a login against the backend. The login requires a user to identify either with
 * email and password or with telephone number and password.
 * @param {Object} loginInfo Information parameters that identify a user account password
 * @param {String} loginInfo.username Must be an valid e-mail or telephone number. The telephone numbers must be valid
 * numbers from any of the countries the service operates in.
 * @param {String} loginInfo.password A string representing the user's password
 * @param {Object} loginInfo.cookie An object to define how the token should be stored in a cookie. If this object is
 * not passed the SDK will not store the token in a cookie.
 * @param {String} loginInfo.cookie.name A string representing the name the cookie will take. Defaults to 'starz-jwt'
 * @param {Number} loginInfo.cookie.expirationTime The time in milliseconds the cookie will be valid for, default to
 * 4 hours.
 * @return {Object} If the login was successful, the function returns an object with basic information of the logged in
 * user account.
 * @example
 * const userCredentials = {
 *  username: “johndoe@email.com”,
 *  password: “mypassword”
 * };
 * const user = await sdk.authenticationManager.login(userCredentials);
 * @example
 * const user = await sdk.authenticationManager.login({
 *  username: “9710506554289”,
 *  password: “anotherUsersPassword”
 * });
 */
async function login(loginInfo) {
  loginInfo = normalizeLoginUsernameParam(loginInfo);
  loginValidation(loginInfo);
  const loginMethod = getLoginMethod(loginInfo);
  return await loginMethod.run.call(this, loginInfo);
}

/**
 * Closes an active session by removing every user related internal information that was generated by a previous
 * [login]{@link module:authenticationManager~login} call.
 */
// FIXME I think this function can be sync, and the 2 awaits inside it can be removed
async function logout(isResetXToken = false) {
  // TODO Handle the case when the login operation created a cookie by removing it
  updateUserData.call(this, null, null);
  // Remove x-token and x-query from layout pages
  isResetXToken && layoutTokenStore.clear();
  // TODO Move these to a decorator (or two, one for persistent and another one for memory cache)
  await Promise.all([
    cache.clear({ database: "layouts" }),
    cache.clear({ database: "devices" }),
    cache.clear({ database: "preferences" }),
    cache.clear({ database: "channelevents" }),
    cache.clear({ database: "deepAddOns" })
  ]);
  memoryCache.remove("layouts");
  memoryCache.remove("devices");
  memoryCache.remove("preferences");
  memoryCache.remove("channelevents");
  memoryCache.remove("deepAddOns");
  this.userManager.resetDeviceAdded();
}

/**
 * This function specifies if there is an active user session or not. An active user session means that the application
 * did a [login]{@link module:web/authenticationManager~login} call previously.
 * @return {Boolean} true if a logged in user exists or false in other case.
 */
async function isLoggedIn() {
  return !!(await token.getToken.call(this));
}

/**
 * Triggers the sign up process by performing a signup request to the backend. The data object to be sent along will
 * need at least to have some user identification such as an e-mail address or a telephone number. Password is not
 * mandatory.
 * @param {Object} signupData - Data object on the user account to be signed up.
 * @param {String} signupData.user - Must be a valid e-mail or telephone number. The telephone numbers must be valid
 * numbers from any of the countries the service operates in.
 * @param {String} signupData.password - A string representing the user's password. Not mandatory.
 * @param {Boolean} signupData.autologin - Boolean that determines whether the signup process will do an inner autologin
 * afterwards.
 * @param {Object} signupData.cookie An object to define how the token should be stored in a cookie. If this object is
 * not passed the SDK will not store the token in a cookie.
 * @param {String} signupData.cookie.name A string representing the name the cookie will take. Defaults to 'starz-jwt'
 * @param {Number} signupData.cookie.expirationTime The time in milliseconds the cookie will be valid for, default to
 * 4 hours.
 * @param {Boolean} autologin - DEPRECATED. Specifies if a login should be done right after the signup completes. Default
 * is true. This way of passing the autologin option is deprecated, it should be passed as part of the signupData config
 * object instead.
 * @public
 */
async function signup(signupData, autologin = true) {
  signupData.autologin = handleAutologinDeprecation(signupData, autologin);
  signupData = normalizeSignupUsernameParam(signupData);
  const signupMethod = getSignupMethod(signupData);
  return await signupMethod.run.call(this, signupData);
}

/**
 * Gets the user geolocation information, returning the country related information from
 * where the user is running the software (ergo, the SDK). It will return this information
 * either by making a request with the machine's IP or, if any public IP address is received
 * as parameter, it will be included among the headers of the request, so that the request
 * will return the geolocation information for that specific IP.
 * @param {String} clientPublicIp - Public IP coming from the client app. In case it's received,
 * it will be sent among the headers of the request to the middleware endpoint.
 * @throws {Error} In case the geolocation call fails or it times out an error will be returned.
 * This function's timeout can be defined by the config parameter config.networkRequestTimeouts.geolocation
 * when instantiating the SDK. If not provided, the default timeout will be used.
 */
/*async function getGeolocation(clientPublicIp) {
  try {
    if (geolocation.get() === null) {
      const geolocationTimeout = this.networkRequestTimeouts.geolocation;
      const response = await this.http.get(`${this.backendHosts.auth}${services.geolocation}`, {
        ...(clientPublicIp !== undefined && { headers: { "X-Forwarded-For": clientPublicIp } }),
        ...(geolocationTimeout !== undefined && { timeout: geolocationTimeout })
      });
      isBrowser && geolocation.update(response.data);
      return response.data;
    }
    return geolocation.get();
  } catch (error) {
    throw new Error(`Couldn't retrieve geolocation -> ${error}`);
  }
}*/

async function getGeolocation(clientPublicIp) {
  try {
    if (geolocation.get() === null) {
      const geolocationTimeout = this.networkRequestTimeouts.geolocation;
      const endpoint = this.backendHosts.clientType  === 'website'
                      ? `${this.backendHosts.spx.geolocation}${services.geolocationSpx}`
                      : `${this.backendHosts.auth}${services.geolocation}`;

      const response = await this.http.get(endpoint, {
        ...(clientPublicIp !== undefined && { headers: { "X-Forwarded-For": clientPublicIp,  "Client-Type": null } }),
        ...(geolocationTimeout !== undefined && { timeout: geolocationTimeout })
      });
      console.log('resposne', response.data);
      isBrowser && geolocation.update(this.backendHosts.clientType  === 'website' ? response.data.country : response.data);
      return response.data;
    }
    return geolocation.get();
  } catch (error) {
    throw new Error(`Couldn't retrieve geolocation -> ${error}`);
  }
}

/**
 * Validates the object which will be sent in /validate-phone request.
 * @param {Object} phoneInfo - validate-phone request body object.
 * @private
 */
function phoneValidation(phoneInfo) {
  validator.addSchema(phoneSchema);
  const validation = validator.validate(phoneInfo, phoneSchema);

  if (validation.errors.length) {
    throw Error(`${errors.user.phoneValidation.INVALID_REQUEST_PARAMS} - ${validation.errors.join(" ")}`);
  }
}

/**
 * Validates if one phone number is valid in one country
 * @param {Number} phoneNumber The phone number to validate
 * @param {Object} config If country param is defined in config object it will be used to force the telephone number to be validated against the rules for a given country
 */
async function validatePhone(phoneNumber, config = {}) {
  phoneValidation({ phoneNumber, config });
  const phoneValidationTimeout = this.networkRequestTimeouts.phoneValidation;
  const country = config.country ? `&country=${config.country}` : "";
  const params = `?phone=${phoneNumber}${country}`;
  const url = `${this.backendHosts.phoneValidation}${services.validatePhone}${params}`;

  try {
    const response = await this.http.get(url, {
      ...(phoneValidationTimeout !== undefined && { timeout: phoneValidationTimeout })
    });
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Ensures phone number provided by the user exists and is a string to proceed with its
 * verification or not.
 * @param {String} phoneNumber - Mobile phone number to be verified.
 */
function validatePhoneVerificationData(phoneNumber) {
  if (!phoneNumber || typeof phoneNumber !== "string") {
    throw Error(errors.authentication.phoneVerification.MISSING_OR_INVALID_PHONE_NUMBER);
  }
}

/**
 * Triggers a request where the user's mobile phone number will be verified.
 * @param {Object} data - Data object containing the data regargding the phone number to be verified.
 * @param {String} data.phoneNumber - Mandatory. Mobile phone number to be verified.
 * @param {String} data.paymentType - Not mandatory. Payment type selected related to which the phone needs to be verified.
 * @param {String} language - Two character language code that needs to be sent ('en' for English, 'fr' for French, 'ar' for Arabic, etc)
 * @param {Boolean} repeat - Boolean that determines if the user wants to request the code again for the
 * mobile number provided or not.
 */
async function verifyPhoneNumber(data, language, repeat = false) {
  validatePhoneVerificationData(data.phoneNumber);
  const params = `?repeat=${repeat}&lang=${language}`;
  const url = `${this.backendHosts.auth}${services.phoneVerification}${params}`;
  const requestHeaders = data.paymentType ? await this.getHeaders() : null;
  try {
    const response = await this.http.post(url, data, requestHeaders);
    phoneVerificationData.update.call(this, response.data);
    return await phoneVerificationData.get.call(this);
  } catch (error) {
    this.handleErrorResponse(error);
  }
}
/**
 * Triggers a request where the user's mobile phone number will be checked for eligiblity.
 * @param {Object} data - Data object containing the data regargding the phone number to be checked for eligiblity.
 * @param {String} data.phoneNumber - Mandatory. Mobile phone number to be checked.
 */
async function eligibility(data) {
  validatePhoneVerificationData(data.phoneNumber);
  const type = data.type || "";
  const params = `?phoneNumber=${data.phoneNumber}&type=${type}`;
  const url = `${this.backendHosts.auth}${services.eligibility}${params}`;
  try {
    const response = await this.http.get(url, {});
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Ensures the username value provided exists and is a string to proceed with its
 * checking or not.
 * @param {String} username - String containing user identifier to be checked.
 */
function validateUsernameToCheck(username) {
  if (!username || typeof username !== "string") {
    throw Error(errors.authentication.usernameCheck.MISSING_OR_INVALID_USERNAME);
  }
}

/**
 * Triggers a request where the username provided will be checked to see if it is already registered
 * in the system.
 * @param {String} username - String containing user identifier to be checked.
 */
async function checkUsername(username) {
  validateUsernameToCheck(username);
  const checkUsernameTimeout = this.networkRequestTimeouts.checkUsername;
  const url = `${this.backendHosts.auth}${services.usernameCheck}`;
  try {
    await this.http.post(
      url,
      { username },
      {
        ...(checkUsernameTimeout !== undefined && { timeout: checkUsernameTimeout })
      }
    );
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Returns user's globalUserId in case is cached or null in case there is none.
 * @returns {Promise} Promise that will deliver the user's globalUserId or null when resolved.
 * @public
 */
async function getUserId() {
  return await localUserId.get.call(this);
}

/**
 * Triggers a request to starting a password recovery process
 * @param {String} username - Can be a mobile phone number or Email.
 * @param {String} language - Two character language code that needs to be sent ('en' for English, 'fr' for French, 'ar' for Arabic, etc)
 */
async function requestPasswordRecovery(username, language, reattempting = false) {
  const data = {
    username,
    language,
    reattempting
  };
  try {
    const response = await this.http.post(`${this.backendHosts.auth}${services.password.forgot}`, data);
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Validates the object which will be sent in /password/reset request.
 * @param {Object} resetPasswordInfo - reset password request body object.
 * @private
 */
function resetPasswordValidation(resetPasswordInfo) {
  validator.addSchema(resetPasswordSchema);
  const validation = validator.validate(resetPasswordInfo, resetPasswordSchema);

  if (validation.errors.length) {
    throw Error(
      `${errors.user.resetPasswordValidation.INVALID_RESET_PASSWORD_PARAMS} - ${validation.errors.join(" ")}`
    );
  }
}

/**
 * Triggers a request to starting a password reset process
 * @param {Object} data
 * @param {String} data.username - Mandatory. User name identifier performing the action. Can be email or msisdn.
 * @param {String} data.newPassword - Mandatory. New password value selected by the customer.
 * @param {String} data.confirmationType - Mandatory. Can be "token" (email flow) or "otp" (mobile SMS flow).
 * @param {String} data.confirmationCode - Mandatory. Confirmation token received by the customer by email, or confirmation otp received by SMS.
 * @param {Boolean} data.invalidatePreviousTokens - Not mandatory. In case it's true, it will generate a new token in the response.
 */
async function resetPassword(data) {
  resetPasswordValidation(data);
  try {
    const response = await this.http.post(`${this.backendHosts.auth}${services.password.reset}`, data);
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Triggers a request to starting a password update process
 * @param {Object} data
 * @param {String} data.currentPassword - Mandatory. Current password value assigned to customer.
 * @param {String} data.newPassword - Mandatory. New password value selected by the customer.
 */
async function updatePassword({ currentPassword, newPassword }) {
  try {
    const userId = await getUserId.call(this);
    const requestPath = t(services.password.update, userId);
    const url = `${this.backendHosts.auth}${requestPath}`;
    const requestData = { currentPassword, newPassword };
    const headers = await this.getHeaders.call(this);
    const response = await this.http.post(url, requestData, headers);
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Get activation link data
 * @public
 */
async function activationLink(linkId) {
  try {
    const response = await this.http.get(
      this.buildUrl("auth", services.activationLink + `?linkid=${linkId}`),
      await this.getHeaders.call(this)
    );
    return response.data;
  } catch (error) {
    this.handleErrorResponse(error);
  }
}

/**
 * Authentication related methods for the SDK. This manager can be used to start user sessions with the login function
 * and once logged in, to obtain authorization for different operations for the logged in user.
 */
export default {
  login: toUserCache(login),
  logout: clearUserCache(logout),
  isLoggedIn,
  signup: toUserCache(signup),
  getPlaybackToken,
  getGeolocation,
  validatePhone,
  socialProviders,
  socialAuthenticationErrors,
  verifyPhoneNumber,
  verifyLandline,
  userEligibility,
  ebillingServices,
  userExists,
  checkUsername,
  getUserId,
  requestPasswordRecovery,
  resetPassword,
  updatePassword,
  eligibility,
  activationLink
};
